import React, { createContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "../api/axios";

const DataContext = createContext({});

export const DataProvider = ({ children }) => {
  const [auth, setAuth] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const lastValidationRef = useRef(0);
  const loggingOutRef = useRef(false);

  // Minimum interval between focus-triggered validations (5 seconds)
  const VALIDATION_COOLDOWN_MS = 5000;

 const logout = useCallback(() => {
  if (loggingOutRef.current) return;
  loggingOutRef.current = true;

  //console.log('DataProvider logout: Clearing authentication state...');
  setAuth(null);
  localStorage.removeItem("BOMpersist");
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  sessionStorage.clear();

  window.location.href = "/login";
}, []);

  // Validate the current token against the backend
  const validateSession = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setAuth(null);
      setIsAuthLoading(false);
      return false;
    }

    try {
      const response = await axios.get("/api/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAuth({ user: response.data.user, roles: Array.isArray(response.data.roles)
    ? response.data.roles
    : [], token });
      lastValidationRef.current = Date.now();
      setIsAuthLoading(false);
      return true;
    } catch (err) {
      // Don't call logout() during initial load if there's no valid session —
      // just clear state and let RequireAuth handle the redirect.
      setAuth(null);
      localStorage.removeItem("token");
      localStorage.removeItem("BOMpersist");
      localStorage.removeItem("refreshToken");
      setIsAuthLoading(false);
      return false;
    }
  }, []);

  // Validate session on mount
  useEffect(() => {
    validateSession();
  }, [validateSession]);

  // Re-validate when the window/tab regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const token = localStorage.getItem("token");
        if (!token) {
          // No token — nothing to validate. If user is on a protected route,
          // RequireAuth will redirect them.
          if (auth) {
            setAuth(null);
          }
          return;
        }

        // Debounce: skip if we validated very recently
        if (Date.now() - lastValidationRef.current < VALIDATION_COOLDOWN_MS) {
          return;
        }

        validateSession().then((valid) => {
          if (!valid && auth) {
            // Session was previously valid but is now invalid — force logout
            logout();
          }
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [auth, validateSession, logout]);

  return (
    <DataContext.Provider
      value={{
        auth,
        setAuth,
        isAuthLoading,
        logout,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataContext;
